import React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import useClassifications from "../../hooks/useClassifications";
import { CLASSIFICATION_ICON_OPTIONS, ClassificationIcon } from "../../utils/classificationIcons";
import MaterialIconPicker from "../../utils/MaterialIconPicker";

const blank = {
    name: "",
    label_de: "",
    label_en: "",
    label_fr: "",
    label_it: "",
    icon_name: "",
    value_type: "rating",
    max_value: 5,
    supports_superior: false,
    sort_order: 0,
    is_active: true,
};

function cleanOpt(v) {
    const s = (v || "").trim();
    return s ? s : null;
}

function getCellValue(row, key) {
    if (key === "active") return row.is_active ? 1 : 0;
    if (key === "superior") return row.supports_superior ? 1 : 0;
    return row[key] ?? "";
}

function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);
    const na = typeof va === "number" ? va : Number(va);
    const nb = typeof vb === "number" ? vb : Number(vb);
    if (Number.isFinite(na) && Number.isFinite(nb)) return nb - na;
    return String(vb ?? "").localeCompare(String(va ?? ""), "de", { sensitivity: "base" });
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    return [...array]
        .map((el, idx) => [el, idx])
        .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
        .map((el) => el[0]);
}

export default function ClassificationsPage() {
    const {
        classifications,
        loading,
        error,
        fetchClassifications,
        createClassification,
        updateClassification,
        deleteClassification,
    } = useClassifications();

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("sort_order");
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState(blank);
    const [editing, setEditing] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    const rows = React.useMemo(
        () => stableSort(classifications || [], getComparator(order, orderBy)),
        [classifications, order, orderBy]
    );

    const columns = [
        { id: "id", label: "ID" },
        { id: "icon_name", label: "Icon" },
        { id: "label_de", label: "Label (DE)" },
        { id: "value_type", label: "Typ" },
        { id: "max_value", label: "Max" },
        { id: "superior", label: "Superior" },
        { id: "active", label: "Aktiv" },
        { id: "sort_order", label: "Sortierung" },
    ];

    const openCreate = () => {
        setEditing(null);
        setForm(blank);
        setSubmitError("");
        setOpen(true);
    };

    const openEdit = (classification) => {
        setEditing(classification);
        setForm({
            ...blank,
            ...classification,
            max_value: classification.max_value ?? "",
            supports_superior: !!classification.supports_superior,
            is_active: classification.is_active !== undefined ? !!classification.is_active : true,
        });
        setSubmitError("");
        setOpen(true);
    };

    const handleSave = async () => {
        const name = (form.name || "").trim();
        const label_de = (form.label_de || "").trim();
        if (!name) {
            setSubmitError("Key/Name ist Pflichtfeld.");
            return;
        }
        if (!label_de) {
            setSubmitError("Label (DE) ist Pflichtfeld.");
            return;
        }

        const payload = {
            name,
            label_de,
            label_en: cleanOpt(form.label_en),
            label_fr: cleanOpt(form.label_fr),
            label_it: cleanOpt(form.label_it),
            icon_name: cleanOpt(form.icon_name),
            value_type: form.value_type || "rating",
            max_value: form.value_type === "rating" ? Number(form.max_value || 0) || null : null,
            supports_superior: !!form.supports_superior,
            sort_order: Number(form.sort_order || 0),
            is_active: !!form.is_active,
        };

        setSubmitting(true);
        setSubmitError("");
        try {
            if (editing) await updateClassification(editing.id, payload);
            else await createClassification(payload);
            setOpen(false);
        } catch (e) {
            setSubmitError(e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageContainer
            title="Klassifikationen"
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchClassifications} disabled={loading}>
                        Aktualisieren
                    </Button>
                    <Button variant="contained" onClick={openCreate}>
                        Neue Klassifikation
                    </Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((c) => (
                                    <TableCell key={c.id}>
                                        <TableSortLabel
                                            active={orderBy === c.id}
                                            direction={order}
                                            onClick={() => {
                                                const isAsc = orderBy === c.id && order === "asc";
                                                setOrder(isAsc ? "desc" : "asc");
                                                setOrderBy(c.id);
                                            }}
                                        >
                                            {c.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((c) => (
                                <TableRow key={c.id} hover>
                                    <TableCell>{c.id}</TableCell>
                                    <TableCell>
                                        {c.icon_name ? (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <ClassificationIcon iconName={c.icon_name} sx={{ color: "primary.main" }} />
                                                <Typography variant="body2">{c.icon_name}</Typography>
                                            </Box>
                                        ) : "-"}
                                    </TableCell>
                                    <TableCell>{c.label_de || "-"}</TableCell>
                                    <TableCell>{c.value_type}</TableCell>
                                    <TableCell>{c.max_value ?? "-"}</TableCell>
                                    <TableCell>{c.supports_superior ? "Ja" : "Nein"}</TableCell>
                                    <TableCell>{c.is_active ? "Ja" : "Nein"}</TableCell>
                                    <TableCell>{c.sort_order ?? 0}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => openEdit(c)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => {
                                                setDeleteTarget(c);
                                                setDeleteError("");
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{editing ? "Klassifikation bearbeiten" : "Neue Klassifikation anlegen"}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                        <TextField label="Key/Name* (technisch)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                        <TextField label="Label (DE)*" value={form.label_de} onChange={(e) => setForm((f) => ({ ...f, label_de: e.target.value }))} />
                        <TextField label="Label (EN)" value={form.label_en || ""} onChange={(e) => setForm((f) => ({ ...f, label_en: e.target.value }))} />
                        <TextField label="Label (FR)" value={form.label_fr || ""} onChange={(e) => setForm((f) => ({ ...f, label_fr: e.target.value }))} />
                        <TextField label="Label (IT)" value={form.label_it || ""} onChange={(e) => setForm((f) => ({ ...f, label_it: e.target.value }))} />
                        <MaterialIconPicker
                            label="Icon"
                            value={form.icon_name || ""}
                            options={CLASSIFICATION_ICON_OPTIONS}
                            onChange={(value) => setForm((f) => ({ ...f, icon_name: value }))}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Typ</InputLabel>
                            <Select
                                label="Typ"
                                value={form.value_type}
                                onChange={(e) => setForm((f) => ({ ...f, value_type: e.target.value }))}
                            >
                                <MenuItem value="rating">Bewertung/Zahl</MenuItem>
                                <MenuItem value="boolean">Ja/Nein</MenuItem>
                                <MenuItem value="text">Text</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            type="number"
                            label="Maximalwert"
                            value={form.max_value ?? ""}
                            disabled={form.value_type !== "rating"}
                            onChange={(e) => setForm((f) => ({ ...f, max_value: e.target.value }))}
                        />
                        <TextField
                            type="number"
                            label="Sortierung"
                            value={form.sort_order ?? 0}
                            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={!!form.supports_superior} onChange={(e) => setForm((f) => ({ ...f, supports_superior: e.target.checked }))} />}
                            label="Superior auswählbar"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />}
                            label="Aktiv"
                        />
                    </Box>
                    {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={submitting}>
                        {submitting ? "Speichern..." : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>Klassifikation löschen</DialogTitle>
                <DialogContent dividers>
                    {deleteError ? (
                        <Alert severity="error">{deleteError}</Alert>
                    ) : (
                        <Alert severity="warning">
                            Willst du <b>{deleteTarget?.label_de || deleteTarget?.name}</b> wirklich löschen?
                            Zuordnungen zu Firmen werden automatisch entfernt.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Abbrechen</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={async () => {
                            if (!deleteTarget) return;
                            setDeleting(true);
                            setDeleteError("");
                            try {
                                await deleteClassification(deleteTarget.id);
                                setDeleteTarget(null);
                            } catch (e) {
                                setDeleteError(e.message || "Löschen fehlgeschlagen.");
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? "Löschen..." : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
